import React, { useEffect, useRef, useState } from 'react';
import './AgencyDashboard.css';
import api from '../services/api';

const emptyForm = {
  id: '',
  name: '',
  email: '',
  phone: '',
  address: '',
  activation_commission: '',
  renewal_commission: '',
  status: 'active'
};

const AgencyPortal = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [subAgencies, setSubAgencies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [caps, setCaps] = useState({ activation: 0, renewal: 0 });
  const [summary, setSummary] = useState({
    total_accrued: 0,
    available_balance: 0,
    total_withdrawn: 0
  });
  const [withdrawal, setWithdrawal] = useState({
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    amount: ''
  });
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const formErrorRef = useRef(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  });

  const load = async () => {
    const base = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };

    const [listRes, settingsRes, summaryRes] = await Promise.all([
      fetch(`${base}/agency/sub-agencies`, { headers }),
      fetch(`${base}/agency/commission-settings`, { headers }),
      fetch(`${base}/agency/commissions/summary`, { headers })
    ]);

    if (listRes.ok) setSubAgencies(await listRes.json());
    if (settingsRes.ok) {
      const data = await settingsRes.json();
      setCaps({
        activation: Number(data.activation_commission ?? data.first_purchase_rate ?? 0),
        renewal: Number(data.renewal_commission ?? data.renewal_rate ?? 0)
      });
    }
    if (summaryRes.ok) {
      const data = await summaryRes.json();
      setSummary(data.summary || data);
    }
  };

  useEffect(() => {
    load().catch((error) => setMessage(error.message || 'Failed to load agency portal'));
  }, []);

  useEffect(() => {
    if (formError && formErrorRef.current) {
      formErrorRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [formError]);

  const openCreate = () => {
    setForm(emptyForm);
    setShowForm(true);
    setFormError('');
    setMessage('');
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || '',
      email: row.email || '',
      phone: row.phone || '',
      address: row.address || '',
      activation_commission: row.activation_commission ?? row.sales_commission ?? '',
      renewal_commission: row.renewal_commission ?? row.sales_commission ?? '',
      status: row.status || 'active'
    });
    setShowForm(true);
    setFormError('');
    setMessage('');
  };

  const saveSubAgency = async (event) => {
    event.preventDefault();
    const activation = parseFloat(form.activation_commission);
    const renewal = parseFloat(form.renewal_commission);
    setFormError('');
    if (!Number.isFinite(activation) || activation < 0 || activation > caps.activation) {
      setFormError(`Activation % must be between 0 and your activation pool (${caps.activation}%).`);
      return;
    }
    if (!Number.isFinite(renewal) || renewal < 0 || renewal > caps.renewal) {
      setFormError(`Renewal % must be between 0 and your renewal pool (${caps.renewal}%).`);
      return;
    }

    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      address: form.address,
      status: form.status,
      activation_commission: activation,
      renewal_commission: renewal,
      sales_commission: activation
    };

    try {
      if (form.id) {
        const base = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${base}/agency/sub-agencies/${form.id}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'Failed to update sub-agency');
        setMessage(data.message || 'Sub-agency updated');
      } else {
        const data = await api.post('/agency/sub-agencies', payload);
        setMessage(data.message || 'Sub-agency created');
      }
      setFormError('');
      setShowForm(false);
      await load();
    } catch (error) {
      setFormError(error.message || 'Save failed');
    }
  };

  const submitWithdrawal = async (event) => {
    event.preventDefault();
    const amount = parseFloat(withdrawal.amount);
    const available = Number(summary.available_balance || 0);
    if (!Number.isFinite(amount) || amount <= 0 || amount > available) {
      setMessage(`Amount must be between ₹1 and ₹${available.toFixed(2)}.`);
      return;
    }
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${base}/agency/commissions/withdrawals`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(withdrawal)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Withdrawal failed');
      }
      setMessage(data.message || 'Withdrawal submitted');
      setWithdrawal({
        account_holder_name: '',
        account_number: '',
        ifsc_code: '',
        bank_name: '',
        amount: ''
      });
      await load();
    } catch (error) {
      setMessage(error.message || 'Withdrawal failed');
    }
  };

  return (
    <div className={`agency-app ${mobileNavOpen ? 'nav-open' : ''}`}>
      {mobileNavOpen && <div className="agency-backdrop" onClick={() => setMobileNavOpen(false)} />}
      <aside className="agency-sidebar">
        <div className="agency-brand">
          <h1>Track My Child</h1>
          <p>Agency Portal</p>
        </div>
        <nav className="agency-nav">
          <button type="button" className={`agency-nav-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
            Overview
          </button>
          <button type="button" className={`agency-nav-item ${activeTab === 'sub-agencies' ? 'active' : ''}`} onClick={() => setActiveTab('sub-agencies')}>
            Sub-agencies
          </button>
          <button type="button" className={`agency-nav-item ${activeTab === 'commission' ? 'active' : ''}`} onClick={() => setActiveTab('commission')}>
            My commission
          </button>
        </nav>
        <div className="agency-sidebar-footer">
          <button type="button" className="logout" onClick={onLogout}>Logout</button>
        </div>
      </aside>

      <div className="agency-main">
        <header className="agency-topbar">
          <div className="agency-topbar-left">
            <button type="button" className="agency-menu-toggle" onClick={() => setMobileNavOpen((v) => !v)}>
              Menu
            </button>
            <div>
              <h2>{activeTab === 'sub-agencies' ? 'Sub-agencies' : activeTab === 'commission' ? 'My commission' : 'Overview'}</h2>
              <p>{user?.name || user?.email}</p>
            </div>
          </div>
        </header>

        <div className="agency-content">
          {message && (
            <p className={/fail|must|cannot|error/i.test(message) ? 'msg-err' : 'msg-ok'}>{message}</p>
          )}
          <div className="agency-panel">
        {activeTab === 'overview' && (
          <div className="dashboard-overview">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-content">
                  <h3>{caps.activation}%</h3>
                  <p>Your activation pool</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-content">
                  <h3>{caps.renewal}%</h3>
                  <p>Your renewal pool</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-content">
                  <h3>{subAgencies.length}</h3>
                  <p>Sub-agencies</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-content">
                  <h3>₹{Number(summary.total_accrued || 0).toLocaleString()}</h3>
                  <p>Commission kept</p>
                </div>
              </div>
            </div>
            <p className="page-desc">
              You set each sub-agency’s activation % and renewal % from your pool.
              The sub-agency creates schools, assigns devices, and sets the school percentage from their share.
              You keep what is left.
            </p>
          </div>
        )}

        {activeTab === 'sub-agencies' && (
          <div className="schools-management">
            <div className="section-header">
              <h3>Sub-agencies</h3>
              <button type="button" className="btn-primary" onClick={openCreate}>+ Add Sub-agency</button>
            </div>
            <div className="schools-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Activation %</th>
                    <th>Renewal %</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {subAgencies.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center' }}>No sub-agencies yet.</td>
                    </tr>
                  )}
                  {subAgencies.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>{row.email}</td>
                      <td>{row.activation_commission ?? row.sales_commission ?? 0}%</td>
                      <td>{row.renewal_commission ?? row.sales_commission ?? 0}%</td>
                      <td>
                        <span className={`status-badge ${row.status === 'inactive' ? 'inactive' : 'active'}`}>
                          {row.status || 'active'}
                        </span>
                      </td>
                      <td><button type="button" className="btn-action" onClick={() => openEdit(row)}>Edit</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showForm && (
              <div className="modal-overlay">
                <div className="modal-content modal-content-wide">
                  <h4>{form.id ? 'Edit sub-agency' : 'Add sub-agency'}</h4>
                  <p>Percentages come out of your pool. Activation max {caps.activation}%. Renewal max {caps.renewal}%.</p>
                  <form onSubmit={saveSubAgency}>
                    <div className="agency-field-grid">
                      <div className="agency-field">
                        <label className="field-label">Name</label>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                      </div>
                      <div className="agency-field">
                        <label className="field-label">Email</label>
                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                      </div>
                      <div className="agency-field">
                        <label className="field-label">Phone</label>
                        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                      </div>
                      <div className="agency-field">
                        <label className="field-label">Status</label>
                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="agency-field full">
                        <label className="field-label">Address</label>
                        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                      </div>
                      <div className="agency-field">
                        <label className="field-label">Activation % — max {caps.activation}%</label>
                        <input type="number" min="0" max="100" step="0.01" value={form.activation_commission} onChange={(e) => setForm({ ...form, activation_commission: e.target.value })} required />
                      </div>
                      <div className="agency-field">
                        <label className="field-label">Renewal % — max {caps.renewal}%</label>
                        <input type="number" min="0" max="100" step="0.01" value={form.renewal_commission} onChange={(e) => setForm({ ...form, renewal_commission: e.target.value })} required />
                      </div>
                    </div>
                    {formError && (
                      <p className="form-alert" role="alert" ref={formErrorRef}>{formError}</p>
                    )}
                    <div className="modal-actions">
                      <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
                      <button type="submit" className="btn-primary">Save</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'commission' && (
          <div className="dashboard-overview">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-content">
                  <h3>₹{Number(summary.total_accrued || 0).toLocaleString()}</h3>
                  <p>Total earned</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-content">
                  <h3>₹{Number(summary.available_balance || 0).toLocaleString()}</h3>
                  <p>Available</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-content">
                  <h3>₹{Number(summary.total_withdrawn || 0).toLocaleString()}</h3>
                  <p>Withdrawn</p>
                </div>
              </div>
            </div>
            <div className="agency-card-box">
              <h4>Request withdrawal</h4>
              <form onSubmit={submitWithdrawal}>
                <div className="agency-field-grid">
                  <div className="agency-field">
                    <label className="field-label">Account holder</label>
                    <input placeholder="Account holder" value={withdrawal.account_holder_name} onChange={(e) => setWithdrawal({ ...withdrawal, account_holder_name: e.target.value })} required />
                  </div>
                  <div className="agency-field">
                    <label className="field-label">Account number</label>
                    <input placeholder="Account number" value={withdrawal.account_number} onChange={(e) => setWithdrawal({ ...withdrawal, account_number: e.target.value })} required />
                  </div>
                  <div className="agency-field">
                    <label className="field-label">IFSC</label>
                    <input placeholder="IFSC" value={withdrawal.ifsc_code} onChange={(e) => setWithdrawal({ ...withdrawal, ifsc_code: e.target.value })} required />
                  </div>
                  <div className="agency-field">
                    <label className="field-label">Bank name</label>
                    <input placeholder="Bank name" value={withdrawal.bank_name} onChange={(e) => setWithdrawal({ ...withdrawal, bank_name: e.target.value })} />
                  </div>
                  <div className="agency-field">
                    <label className="field-label">Amount</label>
                    <input placeholder="Amount" type="number" min="1" step="0.01" value={withdrawal.amount} onChange={(e) => setWithdrawal({ ...withdrawal, amount: e.target.value })} required />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn-primary">Request withdrawal</button>
                </div>
              </form>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyPortal;
